import { Plus } from 'lucide-react';
import { useSimpleFormIterator, useTranslate } from 'ra-core';
import {
  AddItemButton,
  RemoveItemButton,
  SimpleFormIterator,
  type SimpleFormIteratorProps,
} from '../../components/admin/simple-form-iterator';
import { Button } from '../../components/ui/button';
import { cn } from '../../lib/utils';

export type MoonstarFormIteratorProps = SimpleFormIteratorProps & {
  /** Shows a text button with this label instead of the kit's icon-only add button */
  addLabel?: string;
  /** No spacing between the items */
  dense?: boolean;
  /**
   * The items are `MoonstarFormIteratorEntry`s: align the remove / reorder buttons with their header (the kit pushes
   * them down to the first input below its label). Only this iterator's items, not nested iterators'.
   */
  collapsibleItems?: boolean;
};

/** Kit `SimpleFormIterator` without the "clear all" button, with our add button and spacing. */
export function MoonstarFormIterator({
  addLabel,
  dense,
  collapsibleItems,
  className,
  ...props
}: MoonstarFormIteratorProps) {
  return (
    <SimpleFormIterator
      disableClear
      addButton={<AddButton label={addLabel} />}
      removeButton={<RemoveItemButton />}
      className={cn(
        dense ? '[&>ul]:gap-0' : '[&>ul]:gap-4',
        collapsibleItems &&
          '[&>ul>li>.simple-form-iterator-item-actions]:pt-1.5!',
        className
      )}
      {...props}
    />
  );
}

function AddButton({ label }: { label?: string }) {
  const { add } = useSimpleFormIterator();
  const translate = useTranslate();

  if (!label) {
    return <AddItemButton className="mt-4" />;
  }

  return (
    <Button
      type="button"
      variant="ghost"
      className="mt-4 text-primary"
      onClick={() => add()}
    >
      <Plus />
      {translate(label, { _: label })}
    </Button>
  );
}
